import { ReactNode } from 'react';

import styled from 'styled-components';

import { Stack, StackProps } from '@components/stack';
import { theme } from '@styles/theme';
import { pxToRem } from '@utils/converters';

type MiddleColumnPageProps = {
  space?: StackProps['space'];
};

export const MiddleColumnPage: React.FC<MiddleColumnPageProps> = ({ space = 4, children }) => (
  <Grid>
    <ColumnMiddle space={space}>{children}</ColumnMiddle>
  </Grid>
);

export const Grid = styled.div<{ $hasNoGap?: boolean }>(({ $hasNoGap }) => ({
  display: 'grid',
  gridTemplateColumns: 'repeat(4, 1fr)',
  gridColumnGap: '1em',
  gridRowGap: '1.5em',
  [theme.mediaBreakpoint.md]: {
    gridTemplateColumns: 'repeat(8, 1fr)',
    gridColumnGap: '1.5em',
    gridRowGap: '1.5em',
  },
  [theme.mediaBreakpoint.lg]: {
    gridTemplateColumns: 'repeat(12, 1fr)',
    gridColumnGap: '1.5em',
    gridRowGap: '1.5em',
  },
  [theme.mediaBreakpoint.xl]: {
    gridTemplateColumns: 'repeat(12, 1fr)',
    gridColumnGap: '1.5em',
    gridRowGap: '1.5em',
  },
  ...($hasNoGap && {
    '&&&': {
      gridColumnGap: 0,
      gridRowGap: 0,
    },
  }),
}));

const ColumnMiddle = styled(Stack)({
  gridColumn: '1 /  -1',
  [theme.mediaBreakpoint.md]: {
    gridColumn: '2 /  -2',
  },
  [theme.mediaBreakpoint.lg]: {
    gridColumn: '3 / -3',
  },
  [theme.mediaBreakpoint.xl]: {
    gridColumn: '4 / -4',
  },
});

export const ColumnLeftSidebar = styled(Stack)({
  gridColumn: '1 / span 3',
});

export const ColumnMiddleWithLeftSidebar = styled(Stack)({
  gridColumn: '4 /  -1',
  [theme.mediaBreakpoint.md]: {
    gridColumn: '4 /  -2',
  },
  [theme.mediaBreakpoint.lg]: {
    gridColumn: '4 / -3',
  },
  [theme.mediaBreakpoint.xl]: {
    gridColumn: '4 / -4',
  },
});

type BrandedProps = {
  branded: ReactNode;
};

export const LeftContentRightBrandedPage: React.FC<BrandedProps> = ({ branded, children }) => (
  <Grid $hasNoGap>
    <ContentColumn>{children}</ContentColumn>
    <ImageColumn>{branded}</ImageColumn>
  </Grid>
);

const margin = pxToRem(24);

const ContentColumn = styled(Stack).attrs({
  className: 'content-column',
})({
  marginLeft: margin,
  gridColumn: '1 /  -1',
  marginRight: margin,

  [theme.mediaBreakpoint.lg]: {
    gridColumn: '2 / span 4',
  },
});

const ImageColumn = styled(Stack)({
  display: 'none',
  [theme.mediaBreakpoint.lg]: {
    display: 'unset',
    gridColumn: '7 / -1',
  },
});

type ImageProps = {
  imageSrc: string;
};

export const MobileLogoContainer = styled.div({
  display: 'flex',
  justifyContent: 'center',
  position: 'absolute',
  top: pxToRem(24),
  left: 0,
  right: 0,
  '> svg': {
    width: pxToRem(120),
    height: 'auto',
    aspectRatio: '346 / 80',
  },
  [theme.mediaBreakpoint.lg]: {
    display: 'none',
  },
});

export const BackgroundedImage = styled.div<ImageProps>(({ imageSrc }) => ({
  height: '100vh',
  background: `url(${imageSrc}) center/cover`,
}));

export const ContentLeft = styled(Stack)({
  [theme.mediaBreakpoint.sm]: {
    gridColumn: '1 / span 2',
  },
  [theme.mediaBreakpoint.md]: {
    gridColumn: '1 / span 4',
  },
  [theme.mediaBreakpoint.lg]: {
    gridColumn: '1 / span 6',
  },
});

export const ContentRight = styled(Stack)({
  [theme.mediaBreakpoint.sm]: {
    gridColumn: '3 / -1',
  },
  [theme.mediaBreakpoint.md]: {
    gridColumn: '5 / -1',
  },
  [theme.mediaBreakpoint.lg]: {
    gridColumn: '7 / -1',
  },
});

export const ContentMiddleAndRight = styled(Stack)({
  gridColumn: '4 /  -1',
});
